import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import { Fill, Stroke, Style } from "ol/style"
import { Circle } from "ol/geom"
import Feature from "ol/Feature"
import { fromLonLat } from "ol/proj"
import { map } from "./map"
import { loadTravelTimeTable, pWaveDistance, sWaveDistance } from "./travel_time"

// P/S 波传播圆圈动画:10 FPS 节流,支持开发模式回放(Dev Replay)。
// 图层挂在共享地图上,图层名为 p_wave / s_wave。

const state = {
  frame: null,
  sessionId: 0,
  devMode: false,
  devModeStartTime: null,
  simulationBaseTime: null,
  pWaveLayer: null,
  sWaveLayer: null,
  frameCounter: 0,
  // 每次动画开始时按震中/深度重算
  center3857: null,
  maxSDistance: 0,
}

// 本州/北海道/九州/四国的范围(不含冲绳与小笠原・南鸟岛等远岛:
// 走时表最大传播距离 2000km,对部分震中无法覆盖远岛,几何触发会失效)。
// [minLon, minLat, maxLon, maxLat]
const MAIN_FOUR_ISLANDS_EXTENT = [128.5, 30.5, 145.8, 45.6]
const MAIN_FOUR_CORNERS_3857 = [
  fromLonLat([MAIN_FOUR_ISLANDS_EXTENT[0], MAIN_FOUR_ISLANDS_EXTENT[1]]),
  fromLonLat([MAIN_FOUR_ISLANDS_EXTENT[0], MAIN_FOUR_ISLANDS_EXTENT[3]]),
  fromLonLat([MAIN_FOUR_ISLANDS_EXTENT[2], MAIN_FOUR_ISLANDS_EXTENT[3]]),
  fromLonLat([MAIN_FOUR_ISLANDS_EXTENT[2], MAIN_FOUR_ISLANDS_EXTENT[1]]),
]

// S 波圆是否已覆盖四大主岛范围(圆包含 bbox 四角,即可视意义上波前已扫过全境)
function waveCoversMainFourIslands(radius3857) {
  return MAIN_FOUR_CORNERS_3857.every(([x, y]) => {
    const dx = x - state.center3857[0]
    const dy = y - state.center3857[1]
    return Math.hypot(dx, dy) <= radius3857
  })
}

// —— 样式 ——

function createPWaveStyle() {
  return new Style({
    stroke: new Stroke({
      color: "#0066ff", // 蓝色
      width: 2,
    }),
  })
}

function createSWaveStyle() {
  return new Style({
    stroke: new Stroke({
      color: "#ff0000", // 红色
      width: 2,
    }),
    fill: new Fill({
      color: "rgba(255, 0, 0, 0.1)", // 半透明红色
    }),
  })
}

// 获取麦卡托投影下的半径(近似)
// 3857单位在赤道是米,在这里需要除以 cos(lat) 来补偿纬度变形
function getMercatorRadius(radiusKm, latitude) {
  const radiusMeters = radiusKm * 1000
  return radiusMeters / Math.cos((latitude * Math.PI) / 180)
}

// 创建波传播圆圈图层(在麦卡托投影上是圆,性能最好)
function createWaveLayer(centerLonLat, radiusKm, style, name) {
  if (radiusKm <= 0) return null

  const center3857 = fromLonLat(centerLonLat)
  const radius3857 = getMercatorRadius(radiusKm, centerLonLat[1])

  const feature = new Feature(new Circle(center3857, radius3857))

  const layer = new VectorLayer({
    source: new VectorSource({
      features: [feature],
    }),
    style: style,
  })

  layer.set("name", name)
  return layer
}

// 更新现有图层的几何形状(优化性能,避免重建图层)
function updateWaveGeometry(layer, centerLonLat, radiusKm) {
  if (!layer || radiusKm <= 0) return
  const source = layer.getSource()
  if (!source) return
  const features = source.getFeatures()
  if (features.length === 0) return

  const radius3857 = getMercatorRadius(radiusKm, centerLonLat[1])
  features[0].getGeometry().setRadius(radius3857)
}

export function removeWaveLayers() {
  for (const key of ["pWaveLayer", "sWaveLayer"]) {
    if (state[key]) {
      try {
        map.removeLayer(state[key])
      } catch {
        // 图层已从地图移除时忽略
      }
      state[key] = null
    }
  }
}

export function stopWaveAnimation() {
  state.sessionId += 1
  if (state.frame) {
    cancelAnimationFrame(state.frame)
    state.frame = null
  }
}

// 停止动画并移除图层与时间标签(切换报告时调用)
export function cleanupWaves() {
  stopWaveAnimation()
  removeWaveLayers()
  const timeLabel = document.getElementById("waveTimeLabel")
  if (timeLabel) timeLabel.remove()
}

// 更新波的位置
function updateWavePosition(table, epicenter, depth, originTime, currentTime, isFixed) {
  const elapsedSeconds = (currentTime - originTime.getTime()) / 1000
  if (elapsedSeconds < 0) return

  const center = [epicenter.longitude, epicenter.latitude]
  const pDistance = pWaveDistance(table, depth, elapsedSeconds)
  const sDistance = sWaveDistance(table, depth, elapsedSeconds)

  // P波处理
  if (pDistance > 0) {
    if (state.pWaveLayer) {
      updateWaveGeometry(state.pWaveLayer, center, pDistance)
    } else {
      state.pWaveLayer = createWaveLayer(center, pDistance, createPWaveStyle(), "p_wave")
      if (state.pWaveLayer) map.addLayer(state.pWaveLayer)
    }
  }

  // S波处理
  if (sDistance > 0) {
    if (state.sWaveLayer) {
      updateWaveGeometry(state.sWaveLayer, center, sDistance)
    } else {
      state.sWaveLayer = createWaveLayer(center, sDistance, createSWaveStyle(), "s_wave")
      if (state.sWaveLayer) map.addLayer(state.sWaveLayer)
    }
  }

  // 自动缩放检测:每60帧(约1秒)检查一次
  state.frameCounter += 1
  if (state.frameCounter % 60 === 0 && (state.pWaveLayer || state.sWaveLayer)) {
    const pExtent = state.pWaveLayer ? state.pWaveLayer.getSource().getExtent() : null
    const sExtent = state.sWaveLayer ? state.sWaveLayer.getSource().getExtent() : null
    let totalExtent = pExtent

    if (sExtent) {
      if (totalExtent) {
        totalExtent = [
          Math.min(totalExtent[0], sExtent[0]),
          Math.min(totalExtent[1], sExtent[1]),
          Math.max(totalExtent[2], sExtent[2]),
          Math.max(totalExtent[3], sExtent[3]),
        ]
      } else {
        totalExtent = sExtent
      }
    }

    if (totalExtent) {
      const viewSize = map.getSize()
      const currentRes = map.getView().getResolution()

      if (totalExtent[2] - totalExtent[0] > viewSize[0] * currentRes * 0.9) {
        map.getView().fit(totalExtent, { padding: [50, 50, 50, 50], duration: 500 })
      }
    }
  }

  // 更新时间显示(如果是Dev Mode或者Fixed)
  const timeLabel = document.getElementById("waveTimeLabel")
  if (timeLabel) {
    const date = new Date(currentTime)
    const isDeviceTimezone = localStorage.getItem("deviceTimezone") === "true"
    const timeOptions = { hour12: false }
    if (!isDeviceTimezone) {
      timeOptions.timeZone = "Asia/Tokyo"
    }
    const timeStr =
      date.toLocaleTimeString("ja-JP", timeOptions) + "." + String(date.getMilliseconds()).padStart(3, "0").slice(0, 1)
    let text = `Time: ${timeStr}`
    if (isFixed) text += " (Report Time)"
    if (state.devMode) text += " <i class='bi bi-play-fill'></i>"
    timeLabel.innerHTML = text
  }
}

// 确保地图右上角有走时显示标签
function ensureTimeLabel() {
  let timeLabel = document.getElementById("waveTimeLabel")
  if (!timeLabel) {
    timeLabel = document.createElement("div")
    timeLabel.id = "waveTimeLabel"
    timeLabel.className = "alert alert-light position-absolute top-0 end-0 m-2 p-1 border shadow-sm"
    timeLabel.style.zIndex = 1000
    timeLabel.style.fontSize = "0.8rem"
    map.getTargetElement().appendChild(timeLabel)
  }
  return timeLabel
}

// 启动波传播动画。devReplay 为 true 时从发报时间起模拟 200 秒。
export async function startWaveAnimation(epicenter, depth, originTime, reportTime, { devReplay = false } = {}) {
  stopWaveAnimation()
  const sessionId = state.sessionId

  if (devReplay) {
    state.devMode = true
    state.devModeStartTime = Date.now()
    state.simulationBaseTime = reportTime
  } else {
    state.devMode = false
  }

  const table = await loadTravelTimeTable()
  if (sessionId !== state.sessionId) return
  if (!table) return

  // 冻结判定所需的一次性量:震中投影坐标、走时表允许的最大 S 波距离
  state.center3857 = fromLonLat([epicenter.longitude, epicenter.latitude])
  state.maxSDistance = sWaveDistance(table, depth, Infinity)

  // 限制帧率到 10 FPS(大幅降低GPU负载)
  let lastFrameTime = 0
  const fpsInterval = 1000 / 10

  const animate = (timestamp) => {
    if (!lastFrameTime) lastFrameTime = timestamp
    const elapsed = timestamp - lastFrameTime

    if (elapsed > fpsInterval) {
      lastFrameTime = timestamp - (elapsed % fpsInterval)

      const now = Date.now()
      let effectiveTime

      if (state.devMode) {
        // 开发模式:从发报时间起算,模拟200秒
        const elapsedSinceStart = now - state.devModeStartTime
        if (elapsedSinceStart > 200 * 1000) {
          stopWaveAnimation()
          return
        }
        effectiveTime = state.simulationBaseTime.getTime() + elapsedSinceStart
      } else {
        // 正常模式:当前时间
        effectiveTime = now
      }

      // 超出日本领土判断(仅正常模式):S 波覆盖四大主岛、或已到达走时表
      // 最大距离无法再扩大时,固定显示发报时间的位置并结束动画
      if (!state.devMode) {
        const elapsedSeconds = (effectiveTime - originTime.getTime()) / 1000
        if (elapsedSeconds > 0) {
          const sDistance = sWaveDistance(table, depth, elapsedSeconds)
          if (sDistance > 0) {
            const coveredJapan = waveCoversMainFourIslands(getMercatorRadius(sDistance, epicenter.latitude))
            if (coveredJapan || sDistance >= state.maxSDistance) {
              updateWavePosition(table, epicenter, depth, originTime, reportTime.getTime(), true)
              stopWaveAnimation()
              return
            }
          }
        }
      }

      updateWavePosition(table, epicenter, depth, originTime, effectiveTime, false)
    }

    state.frame = requestAnimationFrame(animate)
  }

  state.frame = requestAnimationFrame(animate)
}

export { ensureTimeLabel }
